export const getUserQueryKey = () => ["user"];

export const getUserByIdQueryKey = (userId: string) => ["user", userId];

export const getUserDataKeysQueryKey = () => ["user", "dataKeys"];

export const baseUserTodosQueryKey = ["user", "todos"];

export const getUserTodosQueryKey = (orgId: string, deviceId?: string) => [
  ...baseUserTodosQueryKey,
  orgId,
  deviceId ?? "all",
];

export const baseUserTodosSummaryQueryKey = ["user", "todosSummary"];

export const getUserTodosSummaryQueryKey = (
  orgId: string,
  deviceId?: string
) => [...baseUserTodosSummaryQueryKey, orgId, deviceId ?? "all"];

export const baseDashboardQueryKey = ["dashboard"];

export const getDashboardQueryKey = (orgId: string, deviceId?: string) => [
  ...baseDashboardQueryKey,
  orgId,
  deviceId ?? "all",
];

export const baseChangeManagementPlansQueryKey = ["change-management-plans"];

export const getChangeManagementPlansQueryKey = (orgId: string) => [
  ...baseChangeManagementPlansQueryKey,
  orgId,
];

export const getChangeManagementPlanQueryKey = (
  orgId: string,
  planId: string
) => [...getChangeManagementPlansQueryKey(orgId), planId];

export const baseQualityEventsQueryKey = ["quality-events"];

export const getQualityEventDefinitionsQueryKey = (orgId: string) => [
  ...baseQualityEventsQueryKey,
  orgId,
  "definitions",
];

export const getQualityEventDefinitionQueryKey = (
  orgId: string,
  qualityEventKey: string
) => [...getQualityEventDefinitionsQueryKey(orgId), qualityEventKey];

export const getQualityEventRecordsQueryKey = (
  orgId: string,
  qualityEventKey: string
) => [...baseQualityEventsQueryKey, orgId, "records", qualityEventKey];

export const getQualityEventRecordQueryKey = (
  orgId: string,
  recordId: string
) => [...baseQualityEventsQueryKey, orgId, "record", recordId];

export const getQualityEventAuditQueryKey = (
  orgId: string,
  recordId: string
) => [...getQualityEventRecordQueryKey(orgId, recordId), "audit"];

export const getQualityEventFilesQueryKey = (orgId: string) => [
  ...baseQualityEventsQueryKey,
  orgId,
  "files",
];

export const getQualityEventDocumentReferencesQueryKey = (orgId: string) => [
  ...baseQualityEventsQueryKey,
  orgId,
  "document-references",
];

export const baseDocumentsQueryKey = ["documents"];

export const baseDocumentTrashQueryKey = ["documentTrash"];

export const getDocumentsQueryKey = (
  deviceId: string,
  options?: {
    queryKeyOrgId?: string;
  }
) => [
  ...baseDocumentsQueryKey,
  deviceId,
  ...(options?.queryKeyOrgId ? [options.queryKeyOrgId] : []),
];

export const getDocumentTrashQueryKey = (orgId: string) => [
  ...baseDocumentTrashQueryKey,
  orgId,
];

export const getTrashedDocumentQueryKey = (orgId: string, docId: string) => [
  ...getDocumentTrashQueryKey(orgId),
  docId,
];

export const getUserSurveyAnswersQueryKey = (userId: string) => [
  "user",
  "surveyAnswers",
  userId,
];

export const getFileQueryKey = (fileId: string) => ["file", fileId];

export const getAllFilesInOrgQueryKey = (deviceId?: string) => [
  "allFilesInOrg",
  deviceId,
];

export const getAssistantProcessesQueryKey = (
  orgId: string,
  deviceId: string
) => ["assistantProcesses", orgId, deviceId];

export const getDocumentTemplatesQueryKey = (
  orgId: string,
  deviceId: string
) => ["documentTemplates", orgId, deviceId];

export const getDocumentTemplateQueryKey = (
  orgId: string,
  deviceId: string,
  templateKey: string
) => ["documentTemplate", orgId, deviceId, templateKey];

export const getDevicesQueryKey = (orgId: string) => ["devices", orgId];

export const getFoldersQueryKey = (orgId: string) => ["folders", orgId];

export const baseDocumentQueryKey = ["document"];

export const getDocumentQueryKey = (docId: string) => [
  ...baseDocumentQueryKey,
  docId,
];

/** Key segment shared by every dynamic-content query, for org-wide invalidation. */
export const DYNAMIC_CONTENT_QUERY_KEY_SEGMENT = "dynamic-content";

export const getDynamicContentQueryKey = (
  docId: string,
  versionId: string = ""
) => [...baseDocumentQueryKey, docId, DYNAMIC_CONTENT_QUERY_KEY_SEGMENT, versionId];

/**
 * Separator for serializing a requested data-key set into one key segment: NUL
 * can't appear in a data key, so distinct sets never collide. Kept as an escape
 * (not a literal byte) so git treats this file as text.
 */
const DATA_KEY_SET_SEPARATOR = "\u0000";

/**
 * Dynamic-content key extended with the exact (sorted) set of requested data
 * keys. Adding/removing a key produces a different key, so react-query refetches
 * automatically.
 */
export const getDynamicContentForKeysQueryKey = (
  docId: string,
  versionId: string = "",
  sortedDataKeys: string[]
) => [
  ...getDynamicContentQueryKey(docId, versionId),
  sortedDataKeys.join(DATA_KEY_SET_SEPARATOR),
];

export const getFrameTemplateForDocumentQueryKey = (
  orgId: string,
  templateKey: string,
  deviceId?: string | null
) => ["frameTemplateForDocument", orgId, templateKey, deviceId ?? "org"];

export const getFrameTemplateQueryKey = (
  orgId: string,
  templateId: string
) => ["frameTemplate", orgId, templateId];

/**
 * Template-preview dynamic content, keyed like
 * `getDynamicContentForKeysQueryKey` on the sorted requested set, but scoped to
 * (org, device) instead of a document. Contains the shared dynamic-content
 * segment so org-wide invalidation picks it up.
 */
export const getTemplateDynamicContentQueryKey = (
  orgId: string,
  deviceId: string = "",
  sortedDataKeys: string[] = []
) => [
  DYNAMIC_CONTENT_QUERY_KEY_SEGMENT,
  "template",
  orgId,
  deviceId,
  sortedDataKeys.join(DATA_KEY_SET_SEPARATOR),
];

export const getTasksQueryKey = (orgId: string, deviceId: string) => [
  "tasks",
  orgId,
  deviceId,
];

export const getDeviceQueryKey = (deviceId: string) => ["device", deviceId];

export const getTraceabilityMatrixQueryKey = (
  orgId: string,
  deviceId: string
) => ["traceabilityMatrix", orgId, deviceId];

export const getDataKeyValueQueryKey = (
  orgId: string,
  deviceId: string,
  dataKey: string
) => ["dataKeyValue", orgId, deviceId, dataKey];

export const getAnswerItemsQueryKey = ({
  orgId,
  itemId,
  type,
}: {
  orgId: string;
  itemId: string;
  type: string;
}) => ["answerItems", orgId, itemId, type];

export const getAnswerItemsByTypeQueryKey = ({
  orgId,
  type,
}: {
  orgId: string;
  type: string;
}) => ["answerItemsByType", orgId, type];

export const getCreateDocumentMutationKey = () => ["createDocument"];

export const getApproversQueryKey = (docId: string, docVersionId: string) => [
  "documentApprovers",
  docId,
  docVersionId,
];

export const getPatchApproverStatusMutationKey = () => ["patchApproverStatus"];

export const getOrganizationUsersQueryKey = ({
  orgId,
  skip,
  take,
}: {
  orgId?: string;
  skip?: number;
  take?: number;
}) => ["organizationUsers", orgId, skip, take];

export const getAssisstantCacheQueryKey = (orgId: string, cacheId: string) => [
  "assistantCache",
  orgId,
  cacheId,
];

export const getParseWebsiteToDeviceQueryKey = (url: string) => [
  "parseWebsiteToDevice",
  url,
];

export const getRoadmapStagesQueryKey = (orgId: string, deviceId: string) => [
  "roadmapStages",
  orgId,
  deviceId,
];

export const getRoadmapCardsQueryKey = (orgId: string, deviceId: string) => [
  "roadmapCards",
  orgId,
  deviceId,
];

export const getRoadmapTasksQueryKey = (orgId: string, deviceId: string) => [
  "roadmapTasks",
  orgId,
  deviceId,
];

export const getAdminTaskDefinitionsQueryKey = () => ["adminTaskDefinitions"];

export const getAdminRoadmapCardsQueryKey = () => ["adminRoadmapCards"];

export const getAdminDocumentTemplatesQueryKey = ({
  scope,
  orgId,
  deviceId,
}: {
  scope: string;
  orgId?: string;
  deviceId?: string;
}) => ["adminDocumentTemplates", scope, orgId, deviceId];

export const getAdminRoadmapStagesQueryKey = ({
  scope,
  orgId,
  deviceId,
}: {
  scope: string;
  orgId?: string;
  deviceId?: string;
}) => ["adminRoadmapStages", scope, orgId, deviceId];

export const getAdminOrganizationsQueryKey = () => ["adminOrganizations"];

export const getAdminDevicesQueryKey = () => ["adminDevices"];

export const getDataKeyDefinitionsQueryKey = () => ["dataKeyDefinitions"];

export const getIntegrationQueryKey = (orgId: string, type: string) => [
  "integration",
  orgId,
  type,
];

export const getMcpConnectionsQueryKey = (orgId: string) => [
  "mcp-connections",
  orgId,
];

export const baseSkillsQueryKey = ["skills"];

export const getSkillsQueryKey = (
  orgId: string,
  filter: string = "all",
  search: string = ""
) => [...baseSkillsQueryKey, orgId, filter, search];

export const getSkillQueryKey = (orgId: string, skillId: string) => [
  "skill",
  orgId,
  skillId,
];

export const getSlackAuthUrlQueryKey = (orgId: string) => [
  "slack",
  "authUrl",
  orgId,
];

export const getLinearAuthUrlQueryKey = (orgId: string) => [
  "linear",
  "authUrl",
  orgId,
];

export const getGithubAuthUrlQueryKey = (orgId: string) => [
  "github",
  "authUrl",
  orgId,
];

export const getJiraAuthUrlQueryKey = (orgId: string) => [
  "jira",
  "authUrl",
  orgId,
];

export const getTaskStatesQueryKey = (orgId: string, deviceId: string) => [
  "taskStates",
  orgId,
  deviceId,
];

export const getTaskStateQueryKey = (
  orgId: string,
  deviceId: string,
  stateId: string
) => ["taskState", orgId, deviceId, stateId];

export const getTaskStateActivityQueryKey = (
  orgId: string,
  deviceId: string,
  stateId: string
) => ["taskStateActivity", orgId, deviceId, stateId];

export const getOrgTasksQueryKey = (orgId: string, scope: string) => [
  "orgTasks",
  orgId,
  scope,
];

export const getFunctionalGroupsQueryKey = (orgId: string) => ["functionalGroups", orgId];

export const getTaskProgramsQueryKey = (orgId: string, scope: string) => [
  "taskPrograms",
  orgId,
  scope,
];

export const getTaskProgramDetailQueryKey = (orgId: string, programId: string) => [
  "taskProgramDetail",
  orgId,
  programId,
];
